import * as React from 'react';
import type { AgencyReportUpload } from '../../MedicareProAPI/Entities/Agency/AgencyReportUpload';
import type { GlobCompanies } from '../../MedicareProAPI/Entities/Glob/GlobCompanies';
import { AgencyReportUploadRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyReportUploadRepository';
import { AgencyAgentContractRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyAgentContractRepository';
import { GlobCompaniesRepository } from '../../MedicareProAPI/Repositories/Glob/GlobCompaniesRepository';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';

type ExtractionResponse = {
  processed: number;
  next_offset: number;
  complete: boolean;
};

export interface ContractManagementImportProps {
  props: any;
  agencyReportUpload: AgencyReportUpload;
  onFileCreate: (agencyReportUpload: AgencyReportUpload) => void;
  handleGoBack: () => void;
  handleProcessedFile: (agencyReportUpload: AgencyReportUpload) => void;
  handleOnFileUpdate: (reportId: number, payload: object) => void;
}

/**
 * Component for Agency upload of an RTS template file.
 *
 * @author Andrew Brown
 */

export const ContractManagementImport = (
  props: ContractManagementImportProps
): React.JSX.Element => {
  const globCompaniesRepository = new GlobCompaniesRepository();
  globCompaniesRepository.setAccessToken(props.props.userAccessToken);

  const agencyReportUploadRepository = new AgencyReportUploadRepository();
  agencyReportUploadRepository.setAccessToken(props.props.userAccessToken);

  const agencyAgentContractRepository = new AgencyAgentContractRepository();
  agencyAgentContractRepository.setAccessToken(props.props.userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.props.userAccessToken);

  const [fileCompanySelected, setFileCompanySelected] = React.useState<GlobCompanies>();
  const [rtsYearSelected, setRtsYearSelected] = React.useState<string>();
  const [file, setFile] = React.useState<any>();
  const [fileSelected, setFileSelected] = React.useState<boolean>(false);
  const [fileTypeIsValid, setFileTypeIsValid] = React.useState<boolean>(true);
  const [fileIsBeingProcessed, setFileIsBeingProcessed] = React.useState<boolean>(false);
  const [extractingRows, setExtractingRows] = React.useState<boolean>(false);
  const [extractingComplete, setExtractingComplete] = React.useState<boolean>(false);
  const [processedCount, setProcessedCount] = React.useState<number>(0);
  const [checkingValidity, setCheckingValidity] = React.useState<boolean>(false);
  const [fileIsValid, setFileIsValid] = React.useState<boolean>(true);

  const [fileUploadError, setFileUploadError] = React.useState<string>();

  const [agencyReportUpload, setAgencyReportUpload] = React.useState<AgencyReportUpload>();

  const [globCompanies, setGlobCompanies] = React.useState<Array<GlobCompanies>>();

  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const [processingRts, setProcessingRts] = React.useState<boolean>(false);
  const [processingStates, setProcessingStates] = React.useState<boolean>(false);
  const [processingProducts, setProcessingProducts] = React.useState<boolean>(false);
  const [processingWritingNumbers, setProcessingWritingNumbers] = React.useState<boolean>(false);
  const [processingMissingContracts, setProcessingMissingContracts] =
    React.useState<boolean>(false);
  const [downloadingMissingContracts, setDownloadingMissingContracts] =
    React.useState<boolean>(false);

  const [rtsProcessedCount, setRtsProcessedCount] = React.useState<number>(0);
  const [statesProcessedCount, setStatesProcessedCount] = React.useState<number>(0);
  const [productsProcessedCount, setProductsProcessedCount] = React.useState<number>(0);
  const [writingNumbersProcessedCount, setWritingNumbersProcessedCount] = React.useState<number>(0);

  const date = new Date();
  const thisYear = date.getFullYear();
  const nextYear = thisYear + 1;

  const years = [thisYear, nextYear];

  /**
   * Handles setting the company selected in RTS form
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleFileCompanySelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const index = event.target.value;
    setFileCompanySelected(globCompanies[index]);
  };

  /**
   * Handles setting the RTS year
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleRtsYearSelected = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setRtsYearSelected(value);
  };

  /**
   * Handles setting the file and validating the type
   * @param {React.ChangeEvent<HTMLSelectElement>} event
   */
  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const permittedFileTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const file = event.target.files[0];

    if (!permittedFileTypes.includes(file.type)) {
      setFileTypeIsValid(false);
      return;
    } else {
      setFileTypeIsValid(true);
      setFileSelected(true);
      setFile(file);
    }
  };

  /**
   * Handles uploading the file and saving the AgencyReportUpload
   */
  const handleFileUpload = () => {
    setFileIsBeingProcessed(true);
    setCheckingValidity(true);

    const formData = new FormData();

    formData.append('file', file);
    formData.append('companyId', fileCompanySelected.getCompanyId());
    formData.append('uploadType', 'Contract Management');
    formData.append('planYear', rtsYearSelected);

    agencyReportUploadRepository
      .createAgencyReportUpload(props.props.userId, props.props.agencyId, formData)
      .then((response) => {
        setFileIsValid(true);
        setCheckingValidity(false);
        setAgencyReportUpload(response);
        props.onFileCreate(response);
        extractChunks(response.getId(), 300, 100);
        setExtractingRows(true);
      })
      .catch((error) => {
        setFileIsValid(false);
        setFileUploadError(error.message);
        setCheckingValidity(false);
      });
  };

  const extractChunks = (uploadId, delayMs = 300, maxAttempts = 100) => {
    let attemptCount = 0;
    const runExtraction = async () => {
      try {
        const result: ExtractionResponse =
          await agencyReportUploadRepository.extractAgencyAgentUpload(
            props.props.userId,
            props.props.agencyId,
            uploadId
          );

        console.log(`Processed ${result.processed}, next offset: ${result.next_offset}`);

        if (result.complete) {
          console.log('Extraction complete');
          setExtractingRows(false);
          setExtractingComplete(true);
          // onComplete?.();
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting extraction.');
        }

        setTimeout(runExtraction, delayMs);
      } catch (err) {
        console.error('Extraction error:', err);
        // onError?.(err);
      }
    };

    runExtraction();
  };

  /**
   * Handles clicking go back
   */
  const handleGoBack = () => {
    setFileIsBeingProcessed(false);
    setCheckingValidity(false);
    setFileCompanySelected(undefined);
    setRtsYearSelected(undefined);
    setFileSelected(undefined);
    setFileUploadError(undefined);

    props.handleGoBack();
  };

  /**
   * Handles processing the saved file for RTS operations
   * @param {number} id
   */
  const handleProcessFile = (id) => {
    setIsProcessing(true);

    agencyAgentContractRepository
      .processAgencyAgentContractRtsFromUpload(
        props.props.userId,
        props.props.agencyId,
        agencyReportUpload.getId()
      )
      .then((response) => {
        setFileIsValid(true);
        setCheckingValidity(false);
        setAgencyReportUpload(response);
        setIsProcessing(false);
        props.handleProcessedFile(response);
      });
  };

  const handleProcessRts = (id) => {
    setProcessingRts(true);
    agencyAgentContractRepository
      .processAgencyAgentContractRtsFromUpload(props.props.userId, props.props.agencyId, id)
      .then((response) => {
        props.handleOnFileUpdate(agencyReportUpload.getId(), { rtsProcessed: true });
        setProcessingRts(false);
      });
  };

  const handleProcessStates = (id) => {
    setProcessingStates(true);
    agencyAgentContractRepository
      .processAgencyAgentContractStatesFromUpload(props.props.userId, props.props.agencyId, id)
      .then((response) => {
        props.handleOnFileUpdate(agencyReportUpload.getId(), { stateProcessed: true });
        setProcessingStates(false);
      });
  };

  const handleProcessProducts = (id) => {
    setProcessingProducts(true);
    agencyAgentContractRepository
      .processAgencyAgentContractProductsFromUpload(props.props.userId, props.props.agencyId, id)
      .then((response) => {
        props.handleOnFileUpdate(agencyReportUpload.getId(), { productProcessed: true });
        setProcessingProducts(false);
      });
  };

  const handleProcessWritingNumbers = (id) => {
    setProcessingWritingNumbers(true);
    agencyAgentContractRepository
      .processAgencyAgentContractWritingNumbersFromUpload(
        props.props.userId,
        props.props.agencyId,
        id
      )
      .then((response) => {
        props.handleOnFileUpdate(agencyReportUpload.getId(), { writingNumberProcessed: true });
        setProcessingWritingNumbers(false);
      });
  };

  const handleProcessMissingContracts = (id) => {
    setProcessingMissingContracts(true);
    agencyAgentContractRepository
      .findAgencyAgentContractMissingFromUpload(props.props.userId, props.props.agencyId, id)
      .then((response) => {
        props.handleOnFileUpdate(agencyReportUpload.getId(), {
          missingContractProcessed: true,
          missingContractsExport: response.data,
        });
        setProcessingMissingContracts(false);
      });
  };

  const processRtsInChunks = (
    uploadId: number,
    delayMs = 300,
    maxAttempts = 100,
    chunkSize = 200
  ) => {
    let attemptCount = 0;

    const runProcessing = async () => {
      try {
        const result = await agencyAgentContractRepository.processAgencyAgentContractRtsFromUpload(
          props.props.userId,
          props.props.agencyId,
          uploadId
        );

        setRtsProcessedCount((prev) => prev + result.processed);
        if (result.complete) {
          props.handleOnFileUpdate(uploadId, { contractsProcessed: true });
          setProcessingRts(false);
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting contract processing.');
        }

        setTimeout(runProcessing, delayMs);
      } catch (err) {
        setProcessingRts(false);
      }
    };

    setProcessingRts(true);
    runProcessing();
  };

  const processStatesInChunks = (
    uploadId: number,
    delayMs = 300,
    maxAttempts = 100,
    chunkSize = 200
  ) => {
    let attemptCount = 0;

    const runProcessing = async () => {
      try {
        const result =
          await agencyAgentContractRepository.processAgencyAgentContractStatesFromUpload(
            props.props.userId,
            props.props.agencyId,
            uploadId
          );

        setStatesProcessedCount((prev) => prev + result.processed);
        if (result.complete) {
          props.handleOnFileUpdate(uploadId, { contractsProcessed: true });
          setProcessingStates(false);
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting contract processing.');
        }

        setTimeout(runProcessing, delayMs);
      } catch (err) {
        setProcessingStates(false);
      }
    };

    setProcessingStates(true);
    runProcessing();
  };

  const processProductsInChunks = (
    uploadId: number,
    delayMs = 300,
    maxAttempts = 100,
    chunkSize = 200
  ) => {
    let attemptCount = 0;

    const runProcessing = async () => {
      try {
        const result =
          await agencyAgentContractRepository.processAgencyAgentContractProductsFromUpload(
            props.props.userId,
            props.props.agencyId,
            uploadId
          );

        setProductsProcessedCount((prev) => prev + result.processed);
        if (result.complete) {
          props.handleOnFileUpdate(uploadId, { contractsProcessed: true });
          setProcessingProducts(false);
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting contract processing.');
        }

        setTimeout(runProcessing, delayMs);
      } catch (err) {
        setProcessingProducts(false);
      }
    };

    setProcessingProducts(true);
    runProcessing();
  };

  const processWritingNumbersInChunks = (
    uploadId: number,
    delayMs = 300,
    maxAttempts = 100,
    chunkSize = 200
  ) => {
    let attemptCount = 0;

    const runProcessing = async () => {
      try {
        const result =
          await agencyAgentContractRepository.processAgencyAgentContractWritingNumbersFromUpload(
            props.props.userId,
            props.props.agencyId,
            uploadId
          );

        setWritingNumbersProcessedCount((prev) => prev + result.processed);
        if (result.complete) {
          props.handleOnFileUpdate(uploadId, { contractsProcessed: true });
          setProcessingWritingNumbers(false);
          return;
        }

        attemptCount++;
        if (attemptCount >= maxAttempts) {
          throw new Error('Max attempts reached — aborting contract processing.');
        }

        setTimeout(runProcessing, delayMs);
      } catch (err) {
        setProcessingWritingNumbers(false);
      }
    };

    setProcessingWritingNumbers(true);
    runProcessing();
  };

  const handleMissingContractsDownload = async (id) => {
    try {
      const exportUrl = await webAPI.downloadAgencyAgentContractMissingFromUpload(
        props.props.userId,
        props.props.agencyId,
        id
      );

      if (typeof exportUrl !== 'string' || !exportUrl) {
        throw new Error('Invalid export URL received');
      }

      const form = document.createElement('form');
      form.method = 'post';
      form.action = exportUrl;

      const userIdInput = document.createElement('input');
      userIdInput.type = 'hidden';
      userIdInput.name = 'userId';
      userIdInput.value = props.props.userId;
      form.appendChild(userIdInput);

      document.body.appendChild(form);
      form.submit();

      document.body.removeChild(form);
    } catch (error) {
      console.error('Error downloading missing contracts:', error);
    }
  };

  const getUploadProgress = ({
    offset,
    processedCount,
    total,
    isProcessing,
  }: {
    offset: number | null;
    processedCount: number;
    total: number;
    isProcessing: boolean;
  }): number => {
    const progress = isProcessing ? processedCount : (offset ?? 0);
    return Math.min(progress, total);
  };

  /**
   * Handles getting GlobCompanies to select and setting the AgencyReportUpload if one was passed by parent
   */
  React.useEffect(() => {
    if (globCompanies === undefined) {
      globCompaniesRepository.getGlobCompanies().then((response) => {
        setGlobCompanies(response.getItems());
      });
    }

    if (props.agencyReportUpload) {
      setAgencyReportUpload(props.agencyReportUpload);
      setFileIsBeingProcessed(true);
      setFileTypeIsValid(true);
      setFileSelected(true);
      setCheckingValidity(false);
      setFileIsValid(true);
    } else {
      setAgencyReportUpload(null);
    }
  }, [props.agencyReportUpload]);

  return (
    <div className="mt-4">
      <div className="alert alert-info mb-2 mb-3" style={{ fontSize: '10pt' }}>
        <i className="far fa-comment mr-2"></i>
        The Contract Management upload allows you to bulk update agent contracts and Ready-to-Sell
        statuses for the selected company and plan year. You can also capture state approvals,
        product authorizations, and writing numbers. Download the template to get started.
      </div>

      {!fileIsBeingProcessed && (
        <>
          <div>
            <div className="mb-1">Select company</div>
            <select className="form-control form-control-sm" onChange={handleFileCompanySelected}>
              <option>Select...</option>
              {globCompanies !== undefined &&
                globCompanies.map((item, itemIndex) => {
                  return <option value={itemIndex}>{item.getCompanyName()}</option>;
                })}
            </select>
          </div>

          <div className="mt-4">
            <div className="mb-1">Select RTS year</div>
            <select className="form-control form-control-sm" onChange={handleRtsYearSelected}>
              <option>Select...</option>
              {years.map((item) => {
                return <option value={item}>{item}</option>;
              })}
            </select>
          </div>

          {fileCompanySelected !== undefined && rtsYearSelected !== undefined && (
            <div className="mt-4">
              <div className="mb-1">Select your file</div>
              <input
                type="file"
                className="form-control form-control-sm"
                onChange={handleFileSelected}
              />
              <br />
              {!fileTypeIsValid && (
                <span className="text-danger">
                  That type of file is not supported. Please use a .csv, .xslx, or .xsl file
                </span>
              )}
            </div>
          )}

          {fileCompanySelected !== undefined &&
            rtsYearSelected !== undefined &&
            fileSelected &&
            !fileIsBeingProcessed &&
            fileTypeIsValid && (
              <div className="btn btn-primary mt-4" onClick={handleFileUpload}>
                Upload
              </div>
            )}
        </>
      )}
      {fileSelected && fileTypeIsValid && fileIsBeingProcessed && (
        <>
          <div className="p-3 border border-secondary rounded">
            <h5 className="font-weight-bold">
              <i className="far fa-file-alt mr-2 text-primary"></i>
              {file ? file.name : agencyReportUpload.getFileName()}
            </h5>
            <p>
              {checkingValidity && (
                <>
                  <div>
                    <i className="far fa-spinner fa-spin mr-1"></i>
                    <span>Validating file...</span>
                  </div>
                </>
              )}
              {!checkingValidity &&
                fileIsValid &&
                agencyReportUpload.getContractsStatus() !== 'complete' && (
                  <div className="mt-2">
                    <i className="far fa-check-circle text-success mr-2"></i>File is valid
                  </div>
                )}
              {!checkingValidity && !fileIsValid && (
                <>
                  <i className="far fa-exclamation-circle text-danger mr-2"></i>{' '}
                  <span>
                    There are some problems with your file: <b>{fileUploadError}</b>
                  </span>
                </>
              )}
              {extractingRows && (
                <>
                  <div>
                    <i className="far fa-spinner fa-spin mr-1"></i>
                    <span>Extracting rows...</span>
                  </div>
                </>
              )}
              {extractingComplete && agencyReportUpload.getContractsStatus() !== 'complete' && (
                <div className="mt-2">
                  <i className="far fa-check-circle text-success mr-2"></i>Rows extracted
                </div>
              )}

              {!checkingValidity && fileIsValid && agencyReportUpload !== undefined && (
                <>
                  {agencyReportUpload.getHeaderValues().includes('RTS Status') && (
                    <div className="rounded border border-info mt-3 p-2">
                      <div className="font-weight-bold">Process RTS</div>
                      <div className="rounded bg-light p-2 mt-2">
                        <div>
                          This function will add RTS statuses from your sheet to all the matched
                          agent contracts.
                        </div>
                        {agencyReportUpload.getRtsStatus() !== 'complete' && (
                          <div
                            className="btn btn-sm btn-primary mt-2"
                            onClick={(e) => processRtsInChunks(agencyReportUpload.getId())}
                          >
                            {processingRts && (
                              <>
                                <i className="far fa-spinner fa-spin"></i> Processing...
                              </>
                            )}
                            {!processingRts && !agencyReportUpload.getRtsStatus() && <>Process</>}
                            {!processingRts &&
                              agencyReportUpload.getRtsStatus() === 'processing' && <>Continue</>}
                          </div>
                        )}
                        <div className="mt-2">
                          {agencyReportUpload.getRtsStatus() == 'complete' && (
                            <i className="far fa-check-circle text-success mr-2"></i>
                          )}
                          <b>
                            {getUploadProgress({
                              offset: agencyReportUpload.getRtsOffset(),
                              processedCount: rtsProcessedCount,
                              total: agencyReportUpload.getFileRowsCount(),
                              isProcessing: processingRts,
                            })}
                          </b>{' '}
                          of <b>{agencyReportUpload.getFileRowsCount()}</b> processed
                        </div>
                      </div>
                    </div>
                  )}
                  {agencyReportUpload.getHeaderValues().includes('State') && (
                    <div className="rounded border border-info mt-3 p-2">
                      <div className="font-weight-bold">Process States</div>
                      <div className="rounded bg-light p-2 mt-2">
                        <div>
                          This function will find all the unique states a producer can write in and
                          add those to each contract.
                        </div>
                        {agencyReportUpload.getStatesStatus() !== 'complete' && (
                          <div
                            className="btn btn-sm btn-primary mt-2"
                            onClick={(e) => processStatesInChunks(agencyReportUpload.getId())}
                          >
                            {processingStates && (
                              <>
                                <i className="far fa-spinner fa-spin"></i> Processing...
                              </>
                            )}
                            {!processingStates && !agencyReportUpload.getStatesStatus() && (
                              <>Process</>
                            )}
                            {!processingStates &&
                              agencyReportUpload.getStatesStatus() === 'processing' && (
                                <>Continue</>
                              )}
                          </div>
                        )}
                        <div className="mt-2">
                          {agencyReportUpload.getStatesStatus() == 'complete' && (
                            <i className="far fa-check-circle text-success mr-2"></i>
                          )}
                          <b>
                            {getUploadProgress({
                              offset: agencyReportUpload.getStatesOffset(),
                              processedCount: statesProcessedCount,
                              total: agencyReportUpload.getFileRowsCount(),
                              isProcessing: processingStates,
                            })}
                          </b>{' '}
                          of <b>{agencyReportUpload.getFileRowsCount()}</b> processed
                        </div>
                      </div>
                    </div>
                  )}
                  {agencyReportUpload.getHeaderValues().includes('Product') && (
                    <div className="rounded border border-info mt-3 p-2">
                      <div className="font-weight-bold">Process Products</div>
                      <div className="rounded bg-light p-2 mt-2">
                        <div>
                          This function will find all the unique products a producer can write and
                          add those to each contract.
                        </div>
                        {agencyReportUpload.getProductsStatus() !== 'complete' && (
                          <div
                            className="btn btn-sm btn-primary mt-2"
                            onClick={(e) => processProductsInChunks(agencyReportUpload.getId())}
                          >
                            {processingProducts && (
                              <>
                                <i className="far fa-spinner fa-spin"></i> Processing...
                              </>
                            )}
                            {!processingProducts && !agencyReportUpload.getProductsStatus() && (
                              <>Process</>
                            )}
                            {!processingProducts &&
                              agencyReportUpload.getProductsStatus() === 'processing' && (
                                <>Continue</>
                              )}
                          </div>
                        )}
                        <div className="mt-2">
                          {agencyReportUpload.getProductsStatus() == 'complete' && (
                            <i className="far fa-check-circle text-success mr-2"></i>
                          )}
                          <b>
                            {getUploadProgress({
                              offset: agencyReportUpload.getProductsOffset(),
                              processedCount: productsProcessedCount,
                              total: agencyReportUpload.getFileRowsCount(),
                              isProcessing: processingProducts,
                            })}
                          </b>{' '}
                          of <b>{agencyReportUpload.getFileRowsCount()}</b> processed
                        </div>
                      </div>
                    </div>
                  )}
                  {agencyReportUpload.getHeaderValues().includes('Writing Number') && (
                    <div className="rounded border border-info mt-3 p-2">
                      <div className="font-weight-bold">Process Writing Numbers</div>
                      <div className="rounded bg-light p-2 mt-2">
                        <div>
                          This function will find all the unique writing numbers for each producer
                          and add those to each contract.
                        </div>
                        {agencyReportUpload.getWritingNumbersStatus() !== 'complete' && (
                          <div
                            className="btn btn-sm btn-primary mt-2"
                            onClick={(e) =>
                              processWritingNumbersInChunks(agencyReportUpload.getId())
                            }
                          >
                            {processingWritingNumbers && (
                              <>
                                <i className="far fa-spinner fa-spin"></i> Processing...
                              </>
                            )}
                            {!processingWritingNumbers &&
                              !agencyReportUpload.getWritingNumbersStatus() && <>Process</>}
                            {!processingWritingNumbers &&
                              agencyReportUpload.getWritingNumbersStatus() === 'processing' && (
                                <>Continue</>
                              )}
                          </div>
                        )}
                        <div className="mt-2">
                          {agencyReportUpload.getWritingNumbersStatus() == 'complete' && (
                            <i className="far fa-check-circle text-success mr-2"></i>
                          )}
                          <b>
                            {getUploadProgress({
                              offset: agencyReportUpload.getWritingNumbersOffset(),
                              processedCount: writingNumbersProcessedCount,
                              total: agencyReportUpload.getFileRowsCount(),
                              isProcessing: processingWritingNumbers,
                            })}
                          </b>{' '}
                          of <b>{agencyReportUpload.getFileRowsCount()}</b> processed
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="rounded border border-info mt-3 p-2">
                    <div className="font-weight-bold">Missing Contracts</div>
                    <div className="rounded bg-light p-2 mt-2">
                      <div>
                        This function will find all{' '}
                        {agencyReportUpload.getCompany().getCompanyName()} contracts in the CRM and
                        compare it against this sheet, producing a list of contracts that are
                        missing in the sheet.
                      </div>
                      {!agencyReportUpload.isMissingContractProcessed() && (
                        <div
                          className="btn btn-sm btn-primary mt-2"
                          onClick={(e) => handleProcessMissingContracts(agencyReportUpload.getId())}
                        >
                          {processingMissingContracts && (
                            <>
                              <i className="far fa-spinner fa-spin"></i> Processing...
                            </>
                          )}
                          {!processingMissingContracts && <>Process</>}
                        </div>
                      )}
                      {agencyReportUpload.isMissingContractProcessed() && (
                        <div className="mt-2 p-1 badge badge-success">
                          <i className="far fa-check-circle mr-2"></i>Processed
                        </div>
                      )}
                      {agencyReportUpload.getMissingContractsExport() && (
                        <div
                          className="mt-2 p-1 badge border border-info text-info ml-2"
                          onClick={(e) =>
                            handleMissingContractsDownload(agencyReportUpload.getId())
                          }
                        >
                          <i className="far fa-download mr-2"></i>Download Results
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </p>
          </div>
          <div className="btn border border-secondary mt-3" onClick={handleGoBack}>
            <i className="far fa-chevron-left"></i> Back
          </div>
        </>
      )}
    </div>
  );
};
